// chatWidgetEmbed.js - Fixed to prevent FOUC and timing issues
import { createApp } from 'vue';
import ChatWidget from './components/ChatWidget.vue';
import { setCompanyId } from './lib/appwrite.js';
import { setCompanyId as setFAQCompanyId } from './lib/appwriteFaq.js';
// Import your CSS as a string (Vite will handle this)
import cssContent from './style.css?inline';

const isDevelopment = window.location.hostname === 'http://localhost:5176/' || 
                      window.location.hostname === '127.0.0.1';

if (isDevelopment) {
  console.log('Running in development mode');
}

function waitForDOM() {
  return new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolve);
    } else {
      resolve();
    }
  });
}

let isInitialized = false;

// Custom Element for Shadow DOM encapsulation
class ChatlyWidget extends HTMLElement {
  constructor() {
    super();
    this.vueApp = null;
    this.companyId = null;
    this.isReady = false;
    this.initializationPromise = null;
  }

  connectedCallback() {
    // Get company ID from attribute
    this.companyId = this.getAttribute('company-id') || '';
    
    // ✅ CRITICAL: Hide widget initially to prevent FOUC
    this.style.visibility = 'hidden';
    this.style.opacity = '0';
    
    // Prevent multiple initialization attempts
    if (!this.initializationPromise) {
      this.initializationPromise = this.initializeShadowDOM();
    }
  }

  disconnectedCallback() {
    if (this.vueApp) {
      try {
        this.vueApp.unmount();
      } catch (error) {
        console.warn('⚠️ Error unmounting Vue app:', error);
      }
      this.vueApp = null;
    }
  }

  async initializeShadowDOM() {
    try {
      console.log('🎨 Initializing Shadow DOM widget...');
      
      // Create shadow root with open mode for debugging
      const shadowRoot = this.attachShadow({ mode: 'open' });
      
      // ✅ STEP 1: Preload and inject CSS first
      await this.injectStyles(shadowRoot);
      
      // ✅ STEP 2: Create mount point for Vue app
      const mountPoint = document.createElement('div');
      mountPoint.id = 'chatly-widget-root';
      shadowRoot.appendChild(mountPoint);
      
      // ✅ STEP 3: Set company IDs in appwrite services
      if (this.companyId) {
        setCompanyId(this.companyId);
        setFAQCompanyId(this.companyId);
        console.log('✅ Company ID set in both appwrite services:', this.companyId);
      }
      
      // ✅ STEP 4: Mount Vue app with better error handling
      await this.mountVueApp(mountPoint);
      
      // ✅ STEP 5: Wait for Vue to fully render with better timing
      await this.waitForVueRender();
      
      // ✅ STEP 6: Show widget with smooth transition
      this.showWidget();
      
    } catch (error) {
      console.error('❌ Error initializing shadow DOM widget:', error);
      // Show widget even if there's an error to prevent permanent hiding
      this.showWidget();
    }
  }

  // ✅ IMPROVED: Better style injection with fallback
  async injectStyles(shadowRoot) {
    return new Promise((resolve) => {
      try {
        console.log('🎨 Injecting CSS into Shadow DOM...');
        
        // Create style element with all CSS
        const styleElement = document.createElement('style');
        styleElement.textContent = cssContent;
        
        // Add to shadow root immediately
        shadowRoot.appendChild(styleElement);
        
        // ✅ For inline styles, resolve after processing time
        setTimeout(() => {
          console.log('✅ Inline CSS processed');
          resolve();
        }, 50);
        
      } catch (error) {
        console.error('❌ Error injecting styles:', error);
        resolve(); // Don't block initialization for style errors
      }
    });
  }

  async mountVueApp(mountPoint) {
    try {
      console.log('🚀 Mounting Vue app in Shadow DOM with companyId:', this.companyId);
      
      const app = createApp(ChatWidget, {
        companyId: this.companyId
      });
      
      app.config.errorHandler = (err, vm, info) => {
        console.error('❌ Vue error in Shadow DOM:', err, info);
      };
      
      // Provide shadow root context to components if needed
      app.provide('shadowRoot', this.shadowRoot);
      app.provide('hostElement', this);
      
      this.vueApp = app;
      app.mount(mountPoint);
      
      console.log('✅ Vue app mounted successfully in Shadow DOM');
      
    } catch (error) {
      console.error('❌ Error mounting Vue app in Shadow DOM:', error);
      throw error;
    }
  }

  // ✅ IMPROVED: Better Vue render waiting with safer checks
  async waitForVueRender() {
    return new Promise((resolve) => {
      try {
        // Wait for Vue's nextTick if available
        if (this.vueApp && this.vueApp._instance) {
          // Use Vue's nextTick safely
          const nextTick = this.vueApp._instance.appContext?.app?.config?.globalProperties?.$nextTick;
          
          if (nextTick) {
            nextTick(() => {
              setTimeout(() => {
                console.log('✅ Vue rendering complete');
                resolve();
              }, 100);
            });
          } else {
            // Fallback timing
            setTimeout(() => {
              console.log('✅ Vue rendering complete (fallback)');
              resolve();
            }, 200);
          }
        } else {
          // Simple fallback if Vue app not ready
          setTimeout(() => {
            console.log('✅ Vue rendering complete (simple fallback)');
            resolve();
          }, 150);
        }
      } catch (error) {
        console.warn('⚠️ Error in waitForVueRender, using fallback:', error);
        setTimeout(resolve, 100);
      }
    });
  }

  // ✅ IMPROVED: Show widget with smooth transition
  showWidget() {
    console.log('✨ Showing widget with smooth transition');
    
    // Mark as ready
    this.isReady = true;
    
    // Add smooth transition
    this.style.transition = 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out';
    this.style.visibility = 'visible';
    this.style.opacity = '1';
    
    // Remove transition after animation completes
    setTimeout(() => {
      this.style.transition = '';
    }, 300);
  }

  // Method to update company ID dynamically
  updateCompanyId(newCompanyId) {
    this.companyId = newCompanyId;
    this.setAttribute('company-id', newCompanyId);
    
    if (newCompanyId) {
      setCompanyId(newCompanyId);
      setFAQCompanyId(newCompanyId);
    }
  }

  // ✅ Method to check if widget is ready
  isWidgetReady() {
    return this.isReady;
  }
}

// Register the custom element
if (!customElements.get('chatly-widget')) {
  customElements.define('chatly-widget', ChatlyWidget);
}

// Updated initialization function for manual use
window.initChatWidget = async function(options = {}) {
  if (isInitialized) {
    console.log('Widget already initialized, skipping...');
    return;
  }
  
  console.log('🚀 Initializing chat widget with Shadow DOM and options:', options);
  const companyId = options.companyId || '';
  
  try {
    await waitForDOM();
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Create the custom element
    const widgetElement = document.createElement('chatly-widget');
    if (companyId) {
      widgetElement.setAttribute('company-id', companyId);
    }
    
    // ✅ Add loading class to body while widget initializes
    document.body.classList.add('chatly-widget-loading');
    
    // Add to page (initially hidden)
    document.body.appendChild(widgetElement);
    
    // ✅ Wait for widget to be ready, then remove loading class
    const checkReady = () => {
      if (widgetElement.isWidgetReady && widgetElement.isWidgetReady()) {
        document.body.classList.remove('chatly-widget-loading');
        console.log('✅ Shadow DOM widget ready and visible');
      } else {
        // Check again in 50ms (reduced from 100ms for faster response)
        setTimeout(checkReady, 50);
      }
    };
    
    // Start checking for readiness
    setTimeout(checkReady, 50);
    
    console.log('✅ Shadow DOM widget created successfully');
    isInitialized = true;
    
    return widgetElement;
    
  } catch (error) {
    console.error('❌ Error initializing chat widget:', error);
    // Remove loading class on error
    document.body.classList.remove('chatly-widget-loading');
    throw error;
  }
};

// Auto-initialization with Shadow DOM
let initializationAttempted = false;

async function autoInitialize() {
  if (initializationAttempted) return;
  initializationAttempted = true;
  
  try {
    console.log('🔍 DOM loaded, checking for script tag with data-company-id');
    
    await waitForDOM();
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const scripts = document.querySelectorAll('script');
    const scriptTag = Array.from(scripts).find(script => 
      script.src && script.src.includes('chatly-widget') && script.hasAttribute('data-company-id')
    );
    
    if (scriptTag) {
      const companyId = scriptTag.getAttribute('data-company-id');
      console.log('✅ Found script tag with company ID:', companyId);
      await window.initChatWidget({ companyId });
    } else {
      console.log('ℹ️ No script tag with data-company-id found, checking for existing chatly-widget elements');
      
      // Check if there are any chatly-widget elements already in the DOM
      const existingWidgets = document.querySelectorAll('chatly-widget');
      if (existingWidgets.length === 0) {
        console.log('ℹ️ No chatly-widget elements found, widget not auto-initialized');
      }
    }
  } catch (error) {
    console.error('❌ Auto-initialization error:', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  setTimeout(autoInitialize, 100);
}

// Export for module usage
export { ChatlyWidget };